import React, { useEffect } from 'react';
import { SliderModal } from '@components/molecules/SlideOver';
import { BudgetProposal, BudgetRequest } from '@models/budget.model';
import { ProposalCard } from './ProposalCard';
import { Card } from '@components/atoms/Card';
import { TaskDetailsCard } from './TaskDetailsCard';
import { RecipientsStatusTable } from './RecipientsStatusTable';

interface ProposalHistorySliderProps {
  isOpen: boolean;
  onClose: () => void;
  proposals: BudgetProposal[];
  selectedOrganizationId: string | null | undefined;
  selectedOrganizationName?: string;
  budgetRequest: BudgetRequest;
  organizationId: string | undefined;
  onAcceptProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  onCounterProposal: (proposal: BudgetProposal) => void;
  getRecipientDisplayStatus?: (recipient: any) => {
    status: string;
    isFromProposal: boolean;
    proposal: BudgetProposal | null;
  };
  onRecipientClick?: (
    recipientOrganizationId: string,
    recipientOrganizationName: string,
  ) => void;
  showFullView?: boolean;
}

export const ProposalHistorySlider: React.FC<ProposalHistorySliderProps> = ({
  isOpen,
  onClose,
  proposals,
  selectedOrganizationId,
  selectedOrganizationName,
  budgetRequest,
  organizationId,
  onAcceptProposal,
  onRejectProposal,
  onCounterProposal,
  getRecipientDisplayStatus,
  onRecipientClick,
  showFullView = false,
}) => {
  const isOriginator =
    budgetRequest.originatorOrganizationId === organizationId;
  // Filter proposals for the selected organization (bidirectional conversation)
  const filteredProposals = selectedOrganizationId
    ? proposals.filter((p) => {
        // Always include proposals from the selected recipient
        if (p.proposingOrganizationId === selectedOrganizationId) {
          return true;
        }

        // For our proposals, only include those that are part of the conversation with this specific recipient
        if (p.proposingOrganizationId === organizationId) {
          // If it's a counter proposal, check if it's responding to a proposal from the selected recipient
          if (p.isCounterProposal && p.parentProposalId) {
            const parentProposal = proposals.find(
              (parent) => parent.budgetProposalId === p.parentProposalId,
            );
            return (
              parentProposal?.proposingOrganizationId === selectedOrganizationId
            );
          }
          // If it's an initial proposal from us, it's not specific to this recipient filter
          return false;
        }

        return false;
      })
    : proposals;

  // Sort proposals chronologically (oldest first)
  const sortedProposals = filteredProposals
    .slice()
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

  // Use effect to inject global CSS that overrides modal z-index
  useEffect(() => {
    if (isOpen) {
      // Create a style element with very high specificity
      const styleId = 'proposal-slider-z-index-override';
      let style = document.getElementById(styleId) as HTMLStyleElement;

      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }

      // Add CSS that targets ALL slider elements with motion animations
      // Using extremely high z-index to ensure it's above the main modal
      style.textContent = `
        /* Override z-index for proposal history slider - target motion divs */
        div[class*="fixed"][class*="inset-0"]:has(+ div[class*="absolute"][class*="right-0"] .proposal-history-slider-wrapper),
        div[class*="fixed"][class*="inset-0"]:has(.proposal-history-slider-wrapper) {
          z-index: 99999 !important;
        }
        div[class*="absolute"][class*="right-0"]:has(.proposal-history-slider-wrapper) {
          z-index: 100000 !important;
        }
      `;

      return () => {
        if (style && style.parentNode) {
          style.parentNode.removeChild(style);
        }
      };
    }
  }, [isOpen]);

  return (
    <>
      <SliderModal
        title={
          showFullView
            ? 'Bid Request Details'
            : `Proposal History${selectedOrganizationName ? ` - ${selectedOrganizationName}` : ''}`
        }
        isOpen={isOpen}
        closeSlider={onClose}>
        <div className="flex flex-col gap-6 pb-6 mt-6 proposal-history-slider-wrapper">
          {/* Section 1: Task Details (always shown in full view) */}
          {showFullView && <TaskDetailsCard budgetRequest={budgetRequest} />}

          {/* Section 2: Recipients Status Table (only for originators in full view) */}
          {showFullView && isOriginator && getRecipientDisplayStatus && onRecipientClick && (
            <RecipientsStatusTable
              budgetRequest={budgetRequest}
              proposals={proposals}
              organizationId={organizationId}
              onRecipientClick={onRecipientClick}
              getRecipientDisplayStatus={getRecipientDisplayStatus}
            />
          )}

          {/* Section 3: Proposal History */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
              {showFullView && !isOriginator ? 'Proposal Conversation' : 'Proposal History'}
            </h3>
            {sortedProposals.length === 0 ? (
              <Card className="p-8">
                <div className="text-center text-gray-600 dark:text-gray-400">
                  <p>
                    {showFullView && !isOriginator
                      ? 'No proposals yet'
                      : 'No proposals from this organization'}
                  </p>
                </div>
              </Card>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-5 top-5 bottom-5 w-0.5 bg-gray-200 dark:bg-gray-600 z-0" />

                <div className="flex flex-col gap-6">
                  {sortedProposals.map((proposal) => (
                    <ProposalCard
                      key={proposal.budgetProposalId}
                      proposal={proposal}
                      organizationId={organizationId}
                      budgetRequestStatus={budgetRequest.status}
                      onAccept={onAcceptProposal}
                      onReject={onRejectProposal}
                      onCounter={onCounterProposal}
                      showDocuments={true}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </SliderModal>
    </>
  );
};
